import type { KnowledgeAllowance, KnowledgeBase } from '@ralph/contracts'
import clsx from 'clsx'
import {
  ArrowLeft,
  BookOpen,
  Clock,
  FileText,
  HardDrive,
  Hourglass,
  Layers,
  MessageSquareText,
  PencilLine,
  ShieldCheck,
  Trash2,
  TriangleAlert
} from 'lucide-react'
import { type CSSProperties, type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ApiRequestError } from '@/api/client.ts'
import { GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { Button, EmptyState, IconButton, Readout, Spinner } from '@/components/ui/primitives.tsx'
import { useKnowledgeBase, useKnowledgeBaseDocuments } from '@/hooks/use-knowledge-bases.ts'
import type { Messages } from '@/i18n/index.ts'
import { useI18n } from '@/i18n/index.ts'
import { AccessTab } from './AccessTab.tsx'
import { DeleteBaseDialog, EditBaseDialog, MoveBaseDialog } from './BaseDialogs.tsx'
import { WhyText } from './BaseList.tsx'
import { ConnectedFoldersTab } from './ConnectedFoldersTab.tsx'
import { DocumentsTab, HealthStrip } from './DocumentsTab.tsx'
import { accessLine, formatWindow, hasMoveDestination, opensAsAdministrator } from './knowledge-base.ts'
import { type DocumentFilter, indexHealth } from './knowledge-console.ts'
import { useKnowledgeViewer } from './knowledge-viewer.ts'
import { ReadingSettings, ReprocessOutdated } from './ReadingSettings.tsx'
import { SearchTab } from './SearchTab.tsx'

/**
 * One knowledge base, opened (PRD-26 *The interface* → Knowledge → a base's page).
 *
 * The card says what it is, who owns it (and, for its maintainers, *Move…*), why this reader has
 * it, and how much of it answers — the health sentence first, because whether an agent can cite
 * it is the question before any number. Then the tabs:
 *
 *   readers      Documents · Search
 *   maintainers  Documents · Who can use it · Connected folders · Search
 *
 * The tab lives in the address (`?tab=`), as does the open document (`?doc=`) and folder
 * (`?folder=`), so what someone is looking at can be sent to a colleague rather than described.
 *
 * An administrator who opens a base they could not otherwise use is told so before anything
 * else, and its documents open only when they ask: that read is recorded in the audit trail
 * (D-18), and a record should follow a decision, not a click on a list.
 */

type Tab = 'documents' | 'access' | 'folders' | 'search'

/**
 * The tab labels, built from the ACTIVE catalogue inside the body that renders them.
 *
 * This was a module-scope `Record<Tab, string>` derived from a module-scope catalogue capture —
 * the derived-constant half of PRD-27 F-3. Under a locale switch it kept the previous language
 * with no type error, no runtime error and no failing test.
 */
const tabLabels = (t: Messages['knowledge']): Record<Tab, string> => ({
  documents: t.tabs.documents,
  access: t.tabs.access,
  folders: t.tabs.folders,
  search: t.tabs.search
})

/* Knowledge is the grounded channel throughout the product, so the card's wash comes from that
   one token rather than from a per-base colour the API does not have. */
const GROUNDED_WASH = { '--wash-accent': 'var(--grounded)' } as CSSProperties

export const BasePanel = ({
  baseId,
  fallback,
  allowance,
  uploadMaxBytes,
  onBack,
  onDeleted
}: {
  baseId: string
  /** The row from the list, shown while the base's own reading arrives. */
  fallback: KnowledgeBase | null
  allowance: KnowledgeAllowance | null
  uploadMaxBytes: number | null
  onBack: () => void
  onDeleted: (name: string) => void
}) => {
  const t = useI18n().t.knowledge
  const query = useKnowledgeBase(baseId)
  const base = query.data ?? fallback
  // Existence is not disclosed (D-44): a base this reader cannot use reads exactly like one that
  // was deleted.
  const missing = query.error instanceof ApiRequestError && (query.error.status === 404 || query.error.status === 403)

  // A base that disappears while its row is still in the list (deleted, or no longer shared) says
  // so, rather than going on showing the row's last reading as though nothing happened.
  if (!base || missing) {
    return (
      <div className="px-5 py-5 sm:px-8 lg:px-6">
        <BackButton onBack={onBack} />
        {query.isLoading ? (
          <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
            <Spinner size={12} /> {t.base.loading}
          </p>
        ) : missing ? (
          <EmptyState title={t.base.missing} body={t.base.missingBody} />
        ) : (
          <EmptyState title={t.base.failed} body={t.base.failedBody} />
        )}
      </div>
    )
  }

  // Keyed by the base, so a filter, an open dialog or an administrator's "open its documents"
  // never carries over to the next base chosen from the list.
  return (
    <BaseView
      key={base.id}
      base={base}
      allowance={allowance}
      uploadMaxBytes={uploadMaxBytes}
      onBack={onBack}
      onDeleted={onDeleted}
    />
  )
}

const BackButton = ({ onBack }: { onBack: () => void }) => {
  const t = useI18n().t.knowledge
  return (
    <div className="mb-3 lg:hidden">
      <Button size="sm" variant="ghost" icon={<ArrowLeft size={13} aria-hidden />} onClick={onBack}>
        {t.page.back}
      </Button>
    </div>
  )
}

const BaseView = ({
  base,
  allowance,
  uploadMaxBytes,
  onBack,
  onDeleted
}: {
  base: KnowledgeBase
  allowance: KnowledgeAllowance | null
  uploadMaxBytes: number | null
  onBack: () => void
  onDeleted: (name: string) => void
}) => {
  const titleId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  // Bound inside the body so a language switch re-renders every number on this card; a
  // module-scope capture would freeze them at import with nothing failing (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const viewer = useKnowledgeViewer()
  const [searchParams, setSearchParams] = useSearchParams()
  const canMaintain = base.viewer.canMaintain

  // Below `lg` the list hides the moment a base opens, and the link that opened it goes with it —
  // taking keyboard focus to the top of the document. The base's own name takes it instead.
  useEffect(() => {
    if (!window.matchMedia('(min-width: 64rem)').matches) headingRef.current?.focus()
  }, [])
  const adminOnly = opensAsAdministrator(base)
  const [opened, setOpened] = useState(false)
  const documentsOpen = !adminOnly || opened

  const documents = useKnowledgeBaseDocuments(base.id, documentsOpen)
  const items = documents.data?.items ?? []
  const health = indexHealth(items)
  const synced = items.filter((document) => document.origin !== null).length
  const [filter, setFilter] = useState<DocumentFilter>('all')
  const [dialog, setDialog] = useState<'move' | 'edit' | 'delete' | null>(null)

  const tabs: Tab[] = canMaintain ? ['documents', 'access', 'folders', 'search'] : ['documents', 'search']
  const raw = searchParams.get('tab')
  // A notification about a request to let an agent answer from it lands with `?lending=`, and
  // that request is decided on "Who can use it" — its maintainers' tab, whatever else is named.
  const lending = canMaintain ? searchParams.get('lending') : null
  const tab: Tab = tabs.find((candidate) => candidate === raw) ?? (lending ? 'access' : 'documents')
  const setTab = (next: Tab, extra?: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'documents') params.delete('tab')
    else params.set('tab', next)
    // An open folder belongs to its tab, an open document to the documents, and a request
    // pointed at by a notification to "Who can use it".
    if (next !== 'folders') params.delete('folder')
    if (next !== 'documents') params.delete('doc')
    if (next !== 'access') params.delete('lending')
    extra?.(params)
    setSearchParams(params, { replace: true })
  }

  const openFromSearch = (documentId: string) => {
    setOpened(true)
    setTab('documents', (params) => params.set('doc', documentId))
  }

  return (
    <div className="mx-auto w-full max-w-[64rem] space-y-3 px-4 py-4 sm:px-6">
      <BackButton onBack={onBack} />

      {adminOnly && (
        <div className="flex items-start gap-2.5 rounded-tile border border-rule-strong bg-card-solid/55 px-3.5 py-3">
          <ShieldCheck size={14} strokeWidth={2.2} className="mt-[2px] shrink-0 text-fg-muted" aria-hidden />
          <p className="text-pretty text-[12.5px] leading-[1.5]">
            <span className="block font-semibold">{t.base.adminOnlyTitle}</span>
            <span className="text-fg-muted">{t.base.adminOnly}</span>
          </p>
        </div>
      )}

      <section
        aria-labelledby={titleId}
        className="card accent-wash rounded-card p-4 sm:p-[17px]"
        style={GROUNDED_WASH}
      >
        <div className="flex items-start gap-3">
          <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-tile border border-rule bg-card-solid">
            <BookOpen size={16} strokeWidth={1.9} className="text-grounded" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              ref={headingRef}
              tabIndex={-1}
              className="break-words font-sans text-[16px] font-semibold tracking-[-0.022em] focus:outline-none"
            >
              {base.name}
            </h2>
            <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
              {base.description.trim() === '' ? t.base.noDescription : base.description}
            </p>
          </div>
          {canMaintain && (
            <span className="flex shrink-0 items-center gap-1.5">
              <IconButton label={t.base.editLabel(base.name)} onClick={() => setDialog('edit')}>
                <PencilLine size={13} aria-hidden />
              </IconButton>
              <IconButton label={t.base.deleteLabel(base.name)} tone="alert" onClick={() => setDialog('delete')}>
                <Trash2 size={13} aria-hidden />
              </IconButton>
            </span>
          )}
        </div>

        <div className="mt-3 space-y-1.5">
          <OwnerLine
            base={base}
            viewerId={viewer.id}
            onMove={canMaintain && hasMoveDestination(base.owner, viewer) ? () => setDialog('move') : null}
          />
          <WhyText why={accessLine(fmt, base, viewer.id)} />
        </div>

        {documentsOpen ? (
          <HealthStrip
            health={health}
            loading={documents.isLoading}
            filter={filter}
            synced={synced}
            onFilter={(next) => {
              setFilter(next)
              setTab('documents')
            }}
          />
        ) : (
          <p className="mt-3.5 text-pretty text-[12.5px] leading-[1.45] text-fg-muted">
            {t.base.census(fmt, base.answeringCount, base.documentCount)}
          </p>
        )}

        {canMaintain && (
          <div className="mt-1 space-y-0.5">
            {base.connectedFolders.needsPerson > 0 && (
              <CardNotice
                icon={<TriangleAlert size={12} strokeWidth={2.3} className="shrink-0 text-alert" aria-hidden />}
                action={fmt.t.knowledgeFolders.tab}
                onAction={() => setTab('folders')}
              >
                {fmt.t.knowledgeFolders.baseNotice(fmt, base.connectedFolders.needsPerson)}
              </CardNotice>
            )}
            {base.pendingRequest && (
              <CardNotice
                icon={<Hourglass size={12} strokeWidth={2.2} className="shrink-0 text-primary" aria-hidden />}
                action={t.tabs.access}
                onAction={() => setTab('access')}
              >
                {t.base.waiting(
                  fmt,
                  base.pendingRequest.requestedKind === 'everyone'
                    ? null
                    : base.pendingRequest.groups.map((group) => group.name)
                )}
              </CardNotice>
            )}
            {base.pendingLendingRequests > 0 && (
              <CardNotice
                icon={<MessageSquareText size={12} strokeWidth={2.2} className="shrink-0 text-primary" aria-hidden />}
                action={t.tabs.access}
                onAction={() => setTab('access')}
              >
                {t.base.requests(fmt, base.pendingLendingRequests)}
              </CardNotice>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
          <Stat
            icon={<FileText size={10} strokeWidth={2.2} aria-hidden />}
            label={t.base.stats.documents}
            value={fmt.formatCount(base.documentCount)}
            detail={canMaintain && base.limits ? t.base.stats.of(fmt.formatCount(base.limits.documents)) : null}
          />
          {documentsOpen && documents.data && (
            <Stat
              icon={<Layers size={10} strokeWidth={2.2} aria-hidden />}
              label={t.base.stats.passages}
              value={fmt.formatCount(health.passages)}
            />
          )}
          <Stat
            icon={<HardDrive size={10} strokeWidth={2.2} aria-hidden />}
            label={t.base.stats.space}
            value={fmt.formatBytes(base.storageBytes)}
            detail={canMaintain && base.limits ? t.base.stats.of(fmt.formatBytes(base.limits.storageBytes)) : null}
          />
          <Stat
            icon={<Clock size={10} strokeWidth={2.2} aria-hidden />}
            label={t.base.stats.added}
            value={base.lastIngestedAt ? fmt.formatAgo(base.lastIngestedAt) : t.base.stats.never}
            // Staleness is only a fault in a base that was ever filled — an empty one has not gone
            // out of date, it has not started.
            tone={base.stale && base.documentCount > 0 ? 'alert' : undefined}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-rule pt-3">
          <Readout label={t.base.freshness} value={formatWindow(fmt, base.freshnessSlaHours)} />
          <ReadingSettings base={base} canMaintain={canMaintain} />
          {canMaintain && <ReprocessOutdated base={base} />}
        </div>
      </section>

      <TabList tabs={tabs} active={tab} onSelect={(next) => setTab(next)} base={base} />

      <div role="tabpanel" id={`knowledge-panel-${tab}`} aria-labelledby={`knowledge-tab-${tab}`} className="pt-1">
        {tab === 'documents' && (
          <DocumentsTab
            base={base}
            canMaintain={canMaintain}
            items={items}
            loading={documentsOpen && documents.isLoading}
            failed={documents.isError}
            gated={!documentsOpen}
            onOpenDocuments={() => setOpened(true)}
            filter={filter}
            onFilter={setFilter}
            uploadMaxBytes={uploadMaxBytes}
          />
        )}
        {tab === 'access' && canMaintain && <AccessTab base={base} focusRequestId={lending} />}
        {tab === 'folders' && canMaintain && <ConnectedFoldersTab base={base} canManage={canMaintain} />}
        {tab === 'search' && <SearchTab base={base} canMaintain={canMaintain} onOpenDocument={openFromSearch} />}
      </div>

      {dialog === 'move' && <MoveBaseDialog base={base} allowance={allowance} onClose={() => setDialog(null)} />}
      {dialog === 'edit' && <EditBaseDialog base={base} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && (
        <DeleteBaseDialog base={base} onClose={() => setDialog(null)} onDeleted={(name) => onDeleted(name)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** "Owned by ⬤ Finance EMEA · Move…" — the group is a link to its page, never an id (GRP-102). */
const OwnerLine = ({
  base,
  viewerId,
  onMove
}: {
  base: KnowledgeBase
  viewerId: string | null
  onMove: (() => void) | null
}) => {
  const t = useI18n().t.knowledge
  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-fg-muted">
      <span>{t.base.ownedBy}</span>
      {base.owner.kind === 'group' ? (
        <GroupLabel group={base.owner.group} size={16} to className="font-medium" />
      ) : (
        <span className="font-medium text-fg">{base.owner.id === viewerId ? t.base.you : base.owner.name}</span>
      )}
      {onMove && (
        <>
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={onMove}
            className="rounded-sm text-fg-muted underline decoration-rule-strong underline-offset-2 transition-colors hover:text-fg hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t.base.move}
          </button>
        </>
      )}
    </p>
  )
}

const CardNotice = ({
  icon,
  children,
  action,
  onAction
}: {
  icon: ReactNode
  children: ReactNode
  action: string
  onAction: () => void
}) => (
  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-pretty text-[12.5px] leading-[1.45]">
    {icon}
    <span className="min-w-0">{children}</span>
    <Button size="sm" variant="ghost" onClick={onAction}>
      {action}
    </Button>
  </p>
)

const Stat = ({
  icon,
  label,
  value,
  detail,
  tone
}: {
  icon: ReactNode
  label: string
  value: ReactNode
  detail?: string | null
  tone?: 'alert'
}) => (
  <div>
    <div className="flex items-center gap-1.5 text-fg-subtle">
      {icon}
      <span className="eyebrow text-fg-muted">{label}</span>
    </div>
    <div className="mt-0.5 flex items-baseline gap-1.5">
      <span
        className={clsx(
          'font-mono text-[15px] font-medium tabular-nums tracking-[-0.02em]',
          tone === 'alert' && 'text-alert'
        )}
      >
        {value}
      </span>
      {detail && <span className="readout text-fg-subtle">{detail}</span>}
    </div>
  </div>
)

/**
 * The tabs, as a real tablist: one tab stop, the arrow keys move between tabs (Home and End to
 * the ends), and moving selects — the pattern a keyboard or screen-reader user expects of one.
 */
const TabList = ({
  tabs,
  active,
  onSelect,
  base
}: {
  tabs: Tab[]
  active: Tab
  onSelect: (tab: Tab) => void
  base: KnowledgeBase
}) => {
  const { formatCount, t: messages } = useI18n()
  const t = messages.knowledge
  const labels = tabLabels(t)
  const refs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({})

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabs.length - 1
    if (next === null) return
    event.preventDefault()
    const tab = tabs[next]
    if (!tab) return
    onSelect(tab)
    refs.current[tab]?.focus()
  }

  const waiting = base.pendingRequest !== null || base.pendingLendingRequests > 0

  return (
    <div role="tablist" aria-label={t.tabs.label} className="flex flex-wrap gap-1.5 pt-1">
      {tabs.map((tab, index) => (
        <button
          key={tab}
          ref={(node) => {
            refs.current[tab] = node
          }}
          type="button"
          role="tab"
          id={`knowledge-tab-${tab}`}
          aria-selected={active === tab}
          aria-controls={`knowledge-panel-${tab}`}
          tabIndex={active === tab ? 0 : -1}
          onClick={() => onSelect(tab)}
          onKeyDown={(event) => onKeyDown(event, index)}
          className={clsx(
            'inline-flex h-[30px] shrink-0 items-center rounded-full border px-3.5 text-[12.5px] tracking-[-0.01em]',
            'transition-[color,background-color,border-color] duration-200',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
            active === tab
              ? 'border-rule-strong bg-card-solid font-semibold text-fg'
              : 'border-transparent text-fg-muted hover:border-rule-strong hover:text-fg'
          )}
        >
          {labels[tab]}
          {tab === 'documents' && base.documentCount > 0 && (
            <span className="readout ml-1.5 text-fg-subtle">{formatCount(base.documentCount)}</span>
          )}
          {tab === 'access' && waiting && (
            <>
              <Hourglass size={11} strokeWidth={2.4} className="ml-1.5 text-primary" aria-hidden />
              <span className="sr-only">, {t.tabs.waiting}</span>
            </>
          )}
          {tab === 'folders' && base.connectedFolders.total > 0 && (
            <span className="readout ml-1.5 text-fg-subtle">{formatCount(base.connectedFolders.total)}</span>
          )}
          {tab === 'folders' && base.connectedFolders.needsPerson > 0 && (
            <>
              <TriangleAlert size={11} strokeWidth={2.4} className="ml-1.5 text-alert" aria-hidden />
              <span className="sr-only">, {t.tabs.needsAttention}</span>
            </>
          )}
        </button>
      ))}
    </div>
  )
}
